import { CaretDown } from '@phosphor-icons/react';
import { useNavigate } from 'react-router';
import { INBOX_TABS_FLAG, useFeatureFlag } from '~/hooks/queries/use-feature-flags';
import { useInboxReviewCount } from '~/hooks/queries/use-inbox';
import { PopoverTrigger } from '~/components/ui/popover';
import { Tip } from '~/components/ui/tip';
import { cn } from '~/lib/utils';
import { isAllQuiet, type BarCounts } from './state-mapping';
import { STATE_COLOR_CLASS, STATE_PILL_LABEL } from './state-style';

/** Active states first, then the green done tail — appended rather than
 *  interleaved so the three original dots never move when done appears.
 *  Labels come from the board's pill wording so the bar and the board never
 *  name one state two ways (FRONT-2433 renamed "unread" to "done" there). */
const DOT_ORDER: { key: keyof BarCounts; label: string; blink?: boolean }[] = [
  { key: 'running', label: STATE_PILL_LABEL.running },
  { key: 'asking', label: STATE_PILL_LABEL.asking, blink: true },
  { key: 'stalled', label: STATE_PILL_LABEL.stalled },
  { key: 'ready', label: STATE_PILL_LABEL.ready },
];

/** One 7px dot + mono count per non-zero state — the whole ambient status
 *  read in ~90px (README §2a). "All quiet" replaces the dots entirely when
 *  every chat is read and grey, never a row of zeros. */
function StatusDots({ counts }: { counts: BarCounts }) {
  if (isAllQuiet(counts)) {
    return <span className="text-[13px] text-[var(--ink-2)]">All quiet</span>;
  }
  return (
    <span className="inline-flex items-center gap-[9px]">
      {DOT_ORDER.filter((d) => counts[d.key] > 0).map((d) => (
        <Tip key={d.key} label={`${counts[d.key]} ${d.label}`}>
          <span
            className="inline-flex items-center gap-[5px]"
            data-testid={`topbar-count-${d.key}`}
          >
            <span
              className={cn(
                'size-[7px] shrink-0 rounded-full bg-current',
                STATE_COLOR_CLASS[d.key],
                d.blink && 'eddy-tb-blink',
              )}
            />
            <span className="font-mono text-xs font-medium text-[var(--ink-1)]">
              {counts[d.key]}
            </span>
          </span>
        </Tip>
      ))}
    </span>
  );
}

const BAR_HEIGHTS = [6, 9, 12, 6];

/** The 4-bar equaliser — the only ambient motion in the chrome, and only
 *  while something is genuinely running (README §2b). */
function LiveMeter({ active }: { active: boolean }) {
  if (!active) return null;
  return (
    <span
      className="ml-px inline-flex h-[14px] items-end gap-[2px] border-l border-[var(--line)] pl-[8px]"
      aria-hidden="true"
    >
      {BAR_HEIGHTS.map((h, i) => (
        <span
          key={i}
          className="eddy-tb-bar w-[2.5px] rounded-[1px] bg-[var(--eddy-blue)] opacity-85"
          style={{ height: h }}
        />
      ))}
    </span>
  );
}

/**
 * The whole readout as one sentence, for the trigger's accessible name. The
 * dots carry their meaning in COLOR and their counts in bare numerals, so a
 * screen reader reaching this button would otherwise hear "Chat activity"
 * and nothing about what is waiting. It has to live on the button's own
 * `aria-label`: an `aria-label` on a button discards its subtree from the
 * name computation, so visually-hidden text inside would never be announced.
 * Kept as a substring extension of the old name ("Chat activity: …") because
 * that name is how every existing spec finds this control.
 */
export function summarizeCounts(counts: BarCounts): string {
  const parts = DOT_ORDER.filter((d) => counts[d.key] > 0).map(
    (d) => `${counts[d.key]} ${d.label}`,
  );
  return parts.length ? parts.join(', ') : 'all quiet';
}

/**
 * The status cluster button (README §2) — StatusDots + LiveMeter + a caret
 * that rotates when the activity board is open. Renders as the `Popover`'s
 * trigger, so it must be mounted inside a `<Popover>`; `data-state` (which
 * Radix stamps on the trigger itself) drives both its own open styling and
 * the caret rotation via the `group`/`group-data-*` pair.
 */
export function StatusCluster({
  counts,
  className,
  size = 'default',
}: {
  counts: BarCounts;
  className?: string;
  /**
   * FRONT-1579: `sm` fits the inbox chat modal's tighter eyebrow header row
   * (its own icon controls are 32px) — same two-size convention
   * `NewChatButton` already uses for its footer variant. `default` (the top
   * bar's own trigger) is pixel-identical to before this prop existed.
   */
  size?: 'default' | 'sm';
}) {
  return (
    <PopoverTrigger
      aria-label={`Chat activity: ${summarizeCounts(counts)}`}
      className={cn(
        'group inline-flex shrink-0 items-center gap-[10px] rounded-[11px] border border-[var(--line)]',
        'pr-[11px] pl-[12px] whitespace-nowrap transition-colors',
        'data-[state=open]:border-[var(--line-strong)] data-[state=open]:bg-[var(--bg-3)]',
        size === 'sm' ? 'h-[30px]' : 'h-[38px]',
        className,
      )}
    >
      <StatusDots counts={counts} />
      <LiveMeter active={counts.running > 0} />
      <CaretDown
        size={11}
        className="text-[var(--ink-3)] transition-transform duration-150 group-data-[state=open]:rotate-180"
      />
    </PopoverTrigger>
  );
}

/**
 * FRONT-3227: what is waiting for a yes or no, as a button BESIDE the status
 * cluster — never inside it (the cluster is itself a button, and the activity
 * board's popover hangs off it). Shows only when the tabbed Inbox is on, the
 * request succeeded and there is at least one item; a failed or loading request
 * renders nothing rather than a wrong number. Click goes straight to the
 * Review tab.
 */
export function ReviewCount({ className }: { className?: string }) {
  const navigate = useNavigate();
  const flagOn = useFeatureFlag(INBOX_TABS_FLAG, false);
  const review = useInboxReviewCount({ enabled: flagOn });
  const count = review.data?.count ?? 0;
  if (!flagOn || review.status !== 'success' || count <= 0) return null;
  // The visible "<n> to review" IS the accessible name (no separate aria-label
  // that could say something else); the hover hint says where it goes.
  return (
    <Tip label="Open Review">
      <button
        type="button"
        data-testid="top-bar-review-count"
        onClick={() => navigate('/?inbox=review')}
        className={cn(
          'inline-flex h-[38px] shrink-0 items-center rounded-[11px] border border-[var(--line)] px-[12px]',
          'text-[13px] font-medium whitespace-nowrap text-[var(--ink-1)] transition-colors hover:bg-[var(--bg-3)]',
          className,
        )}
      >
        {count} to review
      </button>
    </Tip>
  );
}
